import { Grid, Typography } from "@mui/material";
import { useReward } from "api/rewards";
import { getRewardType } from "components/Layout/Reward/RewardFulfillmentList/RewardFulfillmentList";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import {
  ModalMode,
  RewardRedemptionModals,
} from "components/Tooltips/RewardRedemptionModals";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REWARDS_EARNED_FROM_TRANSACTION_ID } from "state/tables/types";
import { ListingActions } from "types/listing";
import { Coupon } from "types/rewards";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "./SingleTransaction.styles";

interface Props {
  transactionId: string | undefined;
}

export const EarnedRewardsSection = ({ transactionId }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { setColumnOrdering } = useTablesStateHelpers();
  const { listing, updateListing } = useListing(
    REWARDS_EARNED_FROM_TRANSACTION_ID,
  );
  const { useRewardsFulfillmentList } = useReward();

  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    issuedCoupon?: Coupon;
    issuedReward?: string;
    memberId?: string;
    mode: number;
  }>();

  const { data: rewardsFulfillments } = useRewardsFulfillmentList({
    filters: transactionId
      ? [
          {
            condition: "eq",
            query: "actionCause:transactionId",
            value: transactionId,
          },
        ]
      : [],
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage || 25,
    sortKey: listing.property || "actionCause:transactionId",
  });

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "redeemedRewardId" }),
          hasMinWidthThreshold: true,
          property: "uniqueIdentifier",
          isSortable: false,
        },
        {
          headerText: message({ id: "redemptionDate" }),
          property: "redemptionDate",
          hasMinWidthThreshold: true,
          isSortable: true,
        },
        {
          headerText: message({ id: "rewardName" }),
          property: "name",
          hasMinWidthThreshold: true,
          isSortable: true,
        },
        {
          headerText: message({ id: "rewardType" }),
          property: "rewardType",
          isSortable: true,
        },
        {
          headerText: message({ id: "firstName" }),
          property: "customerData:firstName",
          isSortable: true,
        },
        {
          headerText: message({ id: "lastName" }),
          property: "customerData:lastName",
          isSortable: true,
        },
        {
          headerText: message({ id: "email" }),
          property: "customerData:email",
          hasMinWidthThreshold: true,
          isSortable: true,
        },
        {
          headerText: message({ id: "phoneNumber" }),
          property: "customerData:phone",
          isSortable: true,
        },
        {
          headerText: message({ id: "loyaltyCardNumber" }),
          property: "customerData:loyaltyCardNumber",
          isSortable: true,
        },
        {
          headerText: message({ id: "status" }),
          property: "status",
          isSortable: true,
        },
      ]),
    [message],
  );

  const itemsList = useMemo(
    () =>
      rewardsFulfillments?.items.map((item) => ({
        cells: [
          { content: item.issuedRewardId, property: "uniqueIdentifier" },
          {
            content: OLDate(item.redemptionDate),
            property: "redemptionDate",
          },
          { content: item.name, property: "name" },
          {
            content: message({ id: getRewardType(item) }),
            property: "rewardType",
          },
          {
            content: item.customerData?.firstName,
            property: "customerData:firstName",
            dataHjSuppress: true,
          },
          {
            content: item.customerData?.lastName,
            property: "customerData:lastName",
            dataHjSuppress: true,
          },
          {
            content: item.customerData?.email,
            property: "customerData:email",
            dataHjSuppress: true,
          },
          {
            content: item.customerData?.phone,
            property: "customerData:phone",
            dataHjSuppress: true,
          },
          {
            content: item.customerData?.loyaltyCardNumber,
            property: "customerData:loyaltyCardNumber",
            dataHjSuppress: true,
          },
          { content: message({ id: item.status }), property: "status" },
        ],
        id: item.issuedRewardId,
        metadata: {
          issuedCoupon: item.issuedCoupon,
          memberId: item.customerId,
          rewardId: item.rewardId,
        },
        testId: "rewards-earned-values-list-row",
      })),
    [rewardsFulfillments?.items, message],
  );

  return (
    <Grid item xs={12}>
      <Typography className={classes.sectionTitle} marginBottom={6}>
        {message({ id: "rewardsEarnedFromThisTransaction" })}
      </Typography>
      <OLTable
        disableColumnManager
        setColumnOrdering={setColumnOrdering}
        tableName={REWARDS_EARNED_FROM_TRANSACTION_ID}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpen(true);
                setModal({
                  issuedReward: id as string,
                  mode: ModalMode.ChangeStatus,
                });
              },
              label: message({ id: "changeStatus" }),
            },
            {
              action: (_, item?: Row) => {
                navigate(links.singleMemberRewards(item?.metadata?.memberId));
              },
              hideCondition: (item: Row) => !item?.metadata?.memberId,
              label: message({ id: "seeMemberDetails" }),
            },
            {
              action: (_, item?: Row) => {
                navigate(links.reward(item?.metadata?.rewardId));
              },
              hideCondition: (item: Row) => !item?.metadata?.rewardId,
              label: message({ id: "seeRewardDetails" }),
            },
            {
              label: message({ id: "markAsUnused" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: item?.metadata?.memberId,
                  mode: ModalMode.ReissueAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !item.metadata.issuedCoupon.usedAt,
            },
            {
              label: message({ id: "markAsUsed" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: item?.metadata?.memberId,
                  mode: ModalMode.RedeemAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !!item.metadata.issuedCoupon.usedAt,
            },
          ],
        }}
        data-test-id="rewards-earned-values"
        columns={rewardsFulfillments && headItems}
        rows={rewardsFulfillments && (itemsList as Row[])}
        sort={{
          order: listing.order || "desc",
          property: listing.property,
          rowsPerPage: listing.rowsPerPage || 25,
          page: listing.page,
        }}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            rewardsFulfillments?.total.filtered,
            rewardsFulfillments?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage || 25,
        }}
        onRowClick={(id: string) => {
          navigate(links.rewardsFulfillment(id as string), {
            state: { prevPath: links.transaction(transactionId) },
          });
        }}
      />
      <RewardRedemptionModals
        close={() => setOpen(false)}
        open={open}
        selectedModal={selectedModal}
      />
    </Grid>
  );
};
